import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	SBBox,
	SBCard,
	SBLoading,
	SBOutlinedButton,
	SBTypography,
	SBIcon,
	SBChip,
	SBButton,
} from "modules/Commons";
import { SBNewTable } from 'modules/Commons/SBDataDisplays/SBTable/SBNewTable';
import SBConfirmDialog from "modules/Commons/SBForms/SBConfirmDialog";
import { Radio, RadioGroup, FormControlLabel, FormControl, Alert } from "@mui/material";
import { useHeaders } from "./tableHeaders";
import { useSnackbar } from "modules/Commons/SBOverlays/SBSnackbar";
import {
	useDemandForecastRequester,
} from "modules/DemandForecast/services/DemandForecastRequester";
import { AvailableForecastModel } from "modules/DemandForecast/services/AvailableForecastModel";

export default function ForecastModelSelectionView() {
	const { t } = useTranslation();
	const { error } = useSnackbar();
	const demandForecastRequester = useDemandForecastRequester();
	const [loading, setLoading] = useState(false);
	const [models, setModels] = useState<AvailableForecastModel[]>([]);

	const [configModalOpen, setConfigModalOpen] = useState(false);
	const [selectedModel, setSelectedModel] = useState<AvailableForecastModel | null>(null);
	const [selectedRole, setSelectedRole] = useState<'required' | 'benchmark' | 'none'>('none');

	const fetchModels = () => {
		setLoading(true);
		demandForecastRequester.GET_AVAILABLE_MODELS()
			.then((data) => {
				setModels(data);
			})
			.catch(() => {
				error(t("errorFetchingModels"));
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		fetchModels();
	}, [t, error]);

	const handleUpdateStatus = (id: number, isRequired: boolean, isDefaultBenchmark: boolean) => {
		setModels(prev => prev.map(m => ({
			...m,
			required: isRequired ? (m.id === id) : false,
			isDefaultBenchmark: isDefaultBenchmark ? (m.id === id) : false,
		})));

		demandForecastRequester.UPDATE_MODEL_STATUS(id, { isRequired, isDefaultBenchmark })
			.catch(() => {
				error(t("errorUpdatingModelStatus"));
				fetchModels();
			});
	};

	const handleOpenConfig = React.useCallback((model: AvailableForecastModel) => {
		setSelectedModel(model);
		if (model.required) {
			setSelectedRole('required');
		} else if (model.isDefaultBenchmark) {
			setSelectedRole('benchmark');
		} else {
			setSelectedRole('none');
		}
		setConfigModalOpen(true);
	}, []);

	const handleConfirmConfig = () => {
		if (selectedModel) {
			handleUpdateStatus(
				selectedModel.id,
				selectedRole === 'required',
				selectedRole === 'benchmark'
			);
		}
		setConfigModalOpen(false);
	};

	const { headers } = useHeaders(handleOpenConfig);


	return (
		<SBBox pl={10} pr={4} width="100%">


			{!loading ? (
				models.length > 0 ? (
					<SBNewTable
						title={t("forecastModels")}
						startRows={models}
						defaultColumns={headers}
						onClick={() => {}}
					/>
				) : (
					<SBCard style={{ padding: 0 }}>
						<SBBox p={4} textAlign="center">
							<SBTypography color="gray3.main">
								{t("noModelsAvailable")}
							</SBTypography>
						</SBBox>
					</SBCard>
				)
			) : (
				<SBLoading loading />
			)}

			<SBConfirmDialog
				open={configModalOpen}
				title={t("configureModel")}
				onClose={() => setConfigModalOpen(false)}
				onConfirm={handleConfirmConfig}
				onCancel={() => setConfigModalOpen(false)}
				confirmText={t("save")}
				cancelText={t("cancel")}
				maxWidth="sm"
				fullWidth
			>
				<SBBox display="flex" flexDirection="column" gap={3}>
					<Alert severity="warning" sx={{ borderRadius: "8px", alignItems: "center" }}>
						{t("modelConfigWarning")}
					</Alert>

					<FormControl component="fieldset">
						<RadioGroup
							value={selectedRole}
							onChange={(e) => setSelectedRole(e.target.value as any)}
						>
							<FormControlLabel
								value="required"
								control={<Radio color="primary" />}
								label={
									<SBBox ml={1} my={1}>
										<SBTypography variant="subtitle2" fontWeight={600}>
											{t("forecastRequired")}
										</SBTypography>
										<SBTypography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
											{t("forecastDesc")}
										</SBTypography>
									</SBBox>
								}
								sx={{ alignItems: "flex-start", mb: 1 }}
							/>
							<FormControlLabel
								value="benchmark"
								control={<Radio color="primary" />}
								label={
									<SBBox ml={1} my={1}>
										<SBTypography variant="subtitle2" fontWeight={600}>
											{t("benchmark")}
										</SBTypography>
										<SBTypography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
											{t("benchmarkDesc")}
										</SBTypography>
									</SBBox>
								}
								sx={{ alignItems: "flex-start", mb: 1 }}
							/>
							<FormControlLabel
								value="none"
								control={<Radio color="primary" />}
								label={
									<SBBox ml={1} my={1}>
										<SBTypography variant="subtitle2" fontWeight={600}>
											{t("none")}
										</SBTypography>
									</SBBox>
								}
								sx={{ alignItems: "center" }}
							/>
						</RadioGroup>
					</FormControl>
				</SBBox>
			</SBConfirmDialog>
		</SBBox>
	);
}
